import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { 
  Plus, 
  Play, 
  Pause, 
  Square, 
  MoreHorizontal, 
  Edit, 
  Trash2, 
  Workflow as WorkflowIcon,
  CheckCircle,
  XCircle
} from 'lucide-react'
import { api } from '../lib/api'
import { formatTimestamp, getStatusVariant } from '../lib/utils'
import type { Workflow } from '../types/api'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

export function Workflows() {
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['workflows', statusFilter],
    queryFn: () => api.getWorkflows({
      limit: 20,
      status: statusFilter === 'all' ? undefined : statusFilter
    }),
  })

  const startWorkflowMutation = useMutation({
    mutationFn: (workflowId: number) => api.startWorkflow(workflowId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] })
      queryClient.invalidateQueries({ queryKey: ['organization'] })
    },
  })

  const pauseWorkflowMutation = useMutation({
    mutationFn: (workflowId: number) => api.pauseWorkflow(workflowId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] })
      queryClient.invalidateQueries({ queryKey: ['organization'] })
    },
  })

  const resumeWorkflowMutation = useMutation({
    mutationFn: (workflowId: number) => api.resumeWorkflow(workflowId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] })
      queryClient.invalidateQueries({ queryKey: ['organization'] })
    },
  })

  const stopWorkflowMutation = useMutation({
    mutationFn: (workflowId: number) => api.stopWorkflow(workflowId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] })
      queryClient.invalidateQueries({ queryKey: ['organization'] })
    },
  })

  const deleteWorkflowMutation = useMutation({
    mutationFn: (workflowId: number) => api.deleteWorkflow(workflowId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] })
      queryClient.invalidateQueries({ queryKey: ['organization'] })
    },
  })

  const handleStartWorkflow = (workflowId: number) => {
    startWorkflowMutation.mutate(workflowId)
  }

  const handlePauseWorkflow = (workflowId: number) => {
    pauseWorkflowMutation.mutate(workflowId)
  }

  const handleResumeWorkflow = (workflowId: number) => {
    resumeWorkflowMutation.mutate(workflowId)
  }

  const handleStopWorkflow = (workflowId: number) => {
    stopWorkflowMutation.mutate(workflowId)
  }

  const handleDeleteWorkflow = (workflowId: number) => {
    if (confirm('Are you sure you want to delete this workflow?')) {
      deleteWorkflowMutation.mutate(workflowId)
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'running':
        return <Play className="h-3 w-3" />
      case 'paused':
        return <Pause className="h-3 w-3" />
      case 'finished':
        return <CheckCircle className="h-3 w-3" />
      case 'failed':
        return <XCircle className="h-3 w-3" />
      case 'stopped':
        return <Square className="h-3 w-3" />
      default:
        return <Plus className="h-3 w-3" />
    }
  }

  const canStart = (status: string) => ['created', 'paused', 'stopped', 'failed'].includes(status)
  const canPause = (status: string) => status === 'running'
  const canResume = (status: string) => status === 'paused'
  const canStop = (status: string) => ['running', 'paused'].includes(status)
  const canDelete = (status: string) => status !== 'running'

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Workflows</h1>
          <p className="text-muted-foreground">
            Manage your automated workflows
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline">
            <Link to="/workflows/new">
              <Plus className="h-4 w-4 mr-2" />
              New Workflow (Legacy)
            </Link>
          </Button>
          <Button asChild>
            <Link to="/workflows/new">
              <Plus className="h-4 w-4 mr-2" />
              Build Workflow
            </Link>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center space-x-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="created">Created</SelectItem>
            <SelectItem value="running">Running</SelectItem>
            <SelectItem value="paused">Paused</SelectItem>
            <SelectItem value="finished">Finished</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="stopped">Stopped</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Workflows Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <WorkflowIcon className="h-5 w-5" />
            Workflows
            {data?.pagination && (
              <Badge variant="secondary">{data.pagination.total}</Badge>
            )}
          </CardTitle>
          <CardDescription>
            Manage and monitor your automated workflows
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center space-x-4">
                  <Skeleton className="h-6 w-16" />
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-24" />
                </div>
              ))}
            </div>
          ) : data?.workflows.length ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>URL</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.workflows.map((workflow: Workflow) => (
                  <TableRow key={workflow.id}>
                    <TableCell>
                      <Badge variant={getStatusVariant(workflow.status)} className="flex items-center gap-1 w-fit">
                        {getStatusIcon(workflow.status)}
                        {workflow.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div>
                        <Link
                          to={`/workflows/${workflow.id}/edit`}
                          className="font-medium hover:underline"
                        >
                          {workflow.name}
                        </Link>
                        {workflow.description && (
                          <p className="text-sm text-muted-foreground">
                            {workflow.description}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <a 
                        href={workflow.base_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline text-sm"
                      >
                        {workflow.base_url}
                      </a>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {formatTimestamp(new Date(workflow.updated_at).getTime() / 1000)}
                      </div>
                    </TableCell>
                    <TableCell>
                      {workflow.duration_ms ? (
                        <div className="text-sm">
                          {Math.round(workflow.duration_ms / 1000)}s
                        </div>
                      ) : (
                        <div className="text-sm text-muted-foreground">-</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {canStart(workflow.status) && (
                            <DropdownMenuItem onClick={() => handleStartWorkflow(workflow.id)}>
                              <Play className="h-4 w-4 mr-2" />
                              Start
                            </DropdownMenuItem>
                          )}
                          {canPause(workflow.status) && (
                            <DropdownMenuItem onClick={() => handlePauseWorkflow(workflow.id)}>
                              <Pause className="h-4 w-4 mr-2" />
                              Pause
                            </DropdownMenuItem>
                          )}
                          {canResume(workflow.status) && (
                            <DropdownMenuItem onClick={() => handleResumeWorkflow(workflow.id)}>
                              <Play className="h-4 w-4 mr-2" />
                              Resume
                            </DropdownMenuItem>
                          )}
                          {canStop(workflow.status) && (
                            <DropdownMenuItem onClick={() => handleStopWorkflow(workflow.id)}>
                              <Square className="h-4 w-4 mr-2" />
                              Stop
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem asChild>
                            <Link to={`/workflows/${workflow.id}/edit`}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </Link>
                          </DropdownMenuItem>
                          {canDelete(workflow.status) && (
                            <DropdownMenuItem 
                              onClick={() => handleDeleteWorkflow(workflow.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <WorkflowIcon className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-2 text-sm font-medium">No workflows</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Manage and monitor your automated workflows
              </p>
              <div className="mt-6">
                <Button asChild>
                  <Link to="/workflows/new">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Workflow
                  </Link>
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
